import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete as MUIAutocomplete,
  TextField,
  Box,
  ListItem,
  ListItemText,
  CircularProgress,
  Paper,
} from "@mui/material";

export default function GoogleAutocomplete({
  onLocationSelect,
  placeholder = "",
  country = "gr",
  debounceMs = 400,
  minLength = 1,
  textFieldParams = {},
  disabled,
}) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]); // Array<google.maps.places.AutocompleteSuggestion>
  const [loading, setLoading] = useState(false);

  const timerRef = useRef(null);
  const sessionRef = useRef(null); // AutocompleteSessionToken
  const reqIdRef = useRef(0); // prevent stale updates

  // Debounced fetch using AutocompleteSuggestion (new Autocomplete Data API)
  useEffect(() => {
    let canceled = false;

    async function debouncedFetch(q) {
      clearTimeout(timerRef.current);

      if (!q || q.trim().length < minLength) {
        setOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const thisReq = ++reqIdRef.current;

      timerRef.current = setTimeout(async () => {
        try {
          const { AutocompleteSuggestion, AutocompleteSessionToken } =
            // eslint-disable-next-line no-undef
            await google.maps.importLibrary("places");

          if (!sessionRef.current) {
            sessionRef.current = new AutocompleteSessionToken();
          }

          const { suggestions } =
            await AutocompleteSuggestion.fetchAutocompleteSuggestions({
              input: q,
              includedRegionCodes: [String(country).toUpperCase()],
              // The session token will automatically be included in the first Place.fetchFields()
              sessionToken: sessionRef.current,
            });

          if (canceled || thisReq !== reqIdRef.current) return;
          setOptions(suggestions || []);
        } catch (err) {
          if (!canceled) setOptions([]);
          // Optionally log err
        } finally {
          if (!canceled) setLoading(false);
        }
      }, debounceMs);
    }

    debouncedFetch(inputValue);

    return () => {
      canceled = true;
      clearTimeout(timerRef.current);
    };
  }, [inputValue, country, debounceMs, minLength]);

  // Select a suggestion -> get Place, fetch fields, emit lat/lng
  const handleSelect = async (_, suggestion) => {
    if (!suggestion) return;

    try {
      const pp = suggestion.placePrediction;
      // Keep chosen text visible/editable
      setInputValue(pp?.text?.text || "");

      // Convert prediction to Place and fetch requested fields
      const place = pp.toPlace();
      await place.fetchFields({
        fields: ["location", "displayName", "formattedAddress", "id"],
      });

      const pos = place.location;
      if (!pos) return;

      const lat =
        typeof pos.lat === "function" ? pos.lat() : (pos.lat ?? pos.latitude);
      const lng =
        typeof pos.lng === "function" ? pos.lng() : (pos.lng ?? pos.longitude);
      const address = place.formattedAddress;

      if (lat !== null && lng !== null) {
        onLocationSelect?.({
          lat,
          lng,
          placeId: pp.placeId,
          place,
          address,
        });
      }
    } finally {
      // End the autocomplete billing session after first details fetch
      sessionRef.current = null;
    }
  };

  // Highlight matched ranges from FormattableText.matches
  const renderFormattable = (ft) => {
    if (!ft?.text) return null;
    const text = ft.text;
    const matches = Array.isArray(ft.matches) ? ft.matches : [];
    if (!matches.length) return text;

    const out = [];
    let last = 0;
    matches.forEach(({ startOffset, endOffset }, i) => {
      if (startOffset > last) out.push(text.slice(last, startOffset));
      out.push(
        <Box component="span" key={i} sx={{ fontWeight: 700 }}>
          {text.slice(startOffset, endOffset)}
        </Box>,
      );
      last = endOffset;
    });
    if (last < text.length) out.push(text.slice(last));
    return out;
  };

  return (
    <MUIAutocomplete
      fullWidth
      freeSolo
      value={null} // keep input editable (no selected chip)
      options={options}
      filterOptions={(x) => x} // don't re-filter client-side
      getOptionLabel={(opt) =>
        opt?.placePrediction?.text?.text ??
        opt?.placePrediction?.mainText?.text ??
        ""
      }
      loading={loading}
      disabled={disabled}
      onChange={handleSelect}
      inputValue={inputValue}
      onInputChange={(e, val, reason) => {
        if (reason === "input") setInputValue(val);
        if (reason === "clear") {
          setInputValue("");
          setOptions([]);
        }
      }}
      isOptionEqualToValue={(a, b) =>
        a?.placePrediction?.placeId === b?.placePrediction?.placeId
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          {...textFieldParams}
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
            sx: { height: 40 },
            ...textFieldParams.InputProps,
          }}
        />
      )}
      renderOption={(props, suggestion) => {
        const pp = suggestion.placePrediction;
        return (
          <li {...props} key={pp.placeId}>
            <ListItem disableGutters sx={{ padding: 0 }}>
              <ListItemText
                primary={renderFormattable(pp.text || pp.mainText)}
                primaryTypographyProps={{ noWrap: false }}
              />
            </ListItem>
          </li>
        );
      }}
      PaperComponent={(p) => <Paper {...p} elevation={4} />}
    />
  );
}
